// Neutrální registr „reloadu vrstvy" — umožní rámu okna přenačíst obsah libovolné agendy,
// aniž by o tom agenda musela vědět. Dvě cesty přenačtení (per layerId):
//
//   1) Obsah zaregistruje přesný refetch (useRegisterLayerReload) → reload zavolá jeho handler.
//      Zachová lokální stav vrstvy — používá ListPage, aby reload nezahodil filtry/pohled/stránkování.
//   2) Bez registrace → reload zvýší „nonce" vrstvy; NavigationLayerHost ho promítne do `key`
//      komponenty → čistý remount (čerstvá data, zahodí lokální stav). Univerzální fallback,
//      který funguje pro detaily i libovolnou další agendu bez jediného řádku v agendě.
//
// Store i hooky záměrně nezávisí na navigation/ui/windowing vrstvách → žádný import cyklus
// (ListPage, NavigationLayerHost i WindowActions je importují z tohoto neutrálního místa).

import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { create } from 'zustand';

/** Handler přesného přenačtení obsahu vrstvy. Smí vracet Promise (např. React Query refetch). */
export type LayerReloadHandler = () => void | Promise<void>;

interface LayerReloadState {
  /** Zaregistrované přesné refetch handlery per layerId. */
  readonly handlers: Readonly<Record<string, LayerReloadHandler>>;
  /** Remount „nonce" per layerId — zvýšení vynutí remount komponenty vrstvy (fallback). */
  readonly nonces: Readonly<Record<string, number>>;
  register: (layerId: string, handler: LayerReloadHandler) => void;
  unregister: (layerId: string, handler: LayerReloadHandler) => void;
  /** Přenačte vrstvu: přednostně přes registrovaný handler, jinak remountem (bump nonce). */
  requestReload: (layerId: string) => void;
  /** Uvolní záznamy vrstev, které už neexistují (volá se z desktop sync efektu). */
  prune: (layerIds: readonly string[]) => void;
}

function trimToKeep<T>(
  rec: Readonly<Record<string, T>>,
  keep: ReadonlySet<string>
): Record<string, T> | null {
  const ids = Object.keys(rec);
  if (ids.every((id) => keep.has(id))) return null; // beze změny
  const next: Record<string, T> = {};
  for (const id of ids) if (keep.has(id)) next[id] = rec[id] as T;
  return next;
}

export const useLayerReloadStore = create<LayerReloadState>((set, get) => ({
  handlers: {},
  nonces: {},
  register: (layerId, handler) => set((s) => ({ handlers: { ...s.handlers, [layerId]: handler } })),
  unregister: (layerId, handler) =>
    set((s) => {
      // Odregistruj jen tehdy, když vrstva stále drží tentýž handler — pozdější mount ho mohl přepsat.
      if (s.handlers[layerId] !== handler) return s;
      const next = { ...s.handlers };
      delete next[layerId];
      return { handlers: next };
    }),
  requestReload: (layerId) => {
    const handler = get().handlers[layerId];
    if (handler) {
      void handler();
      return;
    }
    set((s) => ({ nonces: { ...s.nonces, [layerId]: (s.nonces[layerId] ?? 0) + 1 } }));
  },
  prune: (layerIds) =>
    set((s) => {
      const keep = new Set(layerIds);
      const handlers = trimToKeep(s.handlers, keep);
      const nonces = trimToKeep(s.nonces, keep);
      if (!handlers && !nonces) return s;
      return {
        ...(handlers ? { handlers } : {}),
        ...(nonces ? { nonces } : {}),
      };
    }),
}));

/** Ambient layerId pro obsah vrstvy — poskytuje NavigationLayerHost přes LayerReloadScope. */
const LayerIdContext = createContext<string | null>(null);

/** Obalí obsah jedné vrstvy, aby vnořená agenda znala svůj layerId (bez prop-drillingu). */
export function LayerReloadScope({ layerId, children }: { layerId: string; children: ReactNode }) {
  return <LayerIdContext.Provider value={layerId}>{children}</LayerIdContext.Provider>;
}

/**
 * Vrátí layerId aktuální vrstvy (z LayerReloadScope), nebo `null` při renderu mimo vrstvu
 * (shell chrome, hlavní menu, legacy bridge, testy). Slouží obsahu vrstvy ke zjištění vlastní
 * identity bez prop-drillingu — např. aby navigace zevnitř okna zdědila jeho stack (viz `useNavigator`).
 */
export function useCurrentLayerId(): string | null {
  return useContext(LayerIdContext);
}

/**
 * Zaregistruje přesný refetch handler pro aktuální vrstvu (layerId čte z LayerReloadScope).
 * Reload tlačítko okna pak volá tenhle handler místo remountu → zachová lokální stav vrstvy.
 * `handler === undefined` nebo render mimo LayerReloadScope = no-op (bezpečné v testech/pickeru).
 */
export function useRegisterLayerReload(handler: LayerReloadHandler | undefined): void {
  const layerId = useContext(LayerIdContext);
  const ref = useRef(handler);
  ref.current = handler;
  const register = useLayerReloadStore((s) => s.register);
  const unregister = useLayerReloadStore((s) => s.unregister);

  // `handler` schválně není v deps efektu — jeho identitu ignorujeme a voláme vždy nejnovější
  // hodnotu z refu, takže re-render agendy nezpůsobí re-registraci.
  useEffect(() => {
    if (!layerId || !ref.current) return;
    const stable: LayerReloadHandler = () => ref.current?.();
    register(layerId, stable);
    return () => unregister(layerId, stable);
  }, [layerId, register, unregister]);
}

/** Remount „nonce" vrstvy — NavigationLayerHost ho promítne do `key` komponenty. */
export function useLayerReloadNonce(layerId: string): number {
  return useLayerReloadStore((s) => s.nonces[layerId] ?? 0);
}
